import React, { useRef, useEffect, useState, useCallback } from "react";

/**
 * Wraps a horizontally-scrolling table (e.g. MaterialTable) and adds a
 * synchronized scrollbar ABOVE it, so users don't have to scroll to the
 * bottom to move the table sideways.
 *
 * Accessibility (Section 508 / WCAG):
 * - The proxy bar on top is a mouse convenience only, so it is aria-hidden.
 * - The REAL scroll container is made keyboard-operable: it gets tabindex=0,
 *   role="region" and an aria-label, so keyboard users can Tab to it and use
 *   the arrow keys to scroll horizontally (WCAG 2.1.1 Keyboard). This is the
 *   part that actually makes a wide table compliant, not the extra scrollbar.
 *
 * Usage:
 *   <TableTopScrollbar ariaLabel="Summary table, scroll horizontally">
 *     <MaterialTable ... />
 *   </TableTopScrollbar>
 */
export default function TableTopScrollbar({
  children,
  ariaLabel = "Table, scroll horizontally",
  barHeight = 16,
  stickyTop = 0,
}) {
  const containerRef = useRef(null); // wraps the table
  const topBarRef = useRef(null); // the proxy scrollbar shown on top
  const scrollElRef = useRef(null); // the real scrollable element inside the table
  const syncing = useRef(false); // guards against scroll feedback loop
  const [scrollWidth, setScrollWidth] = useState(0);

  // Find the actual horizontally-scrollable descendant of the table.
  const findScrollEl = useCallback(() => {
    const root = containerRef.current;
    if (!root) return null;
    const candidates = root.querySelectorAll("div");
    for (const el of candidates) {
      if (el === topBarRef.current) continue; // skip our own proxy bar
      const ox = window.getComputedStyle(el).overflowX;
      if (
        (ox === "auto" || ox === "scroll") &&
        el.scrollWidth > el.clientWidth + 1
      ) {
        return el;
      }
    }
    return null;
  }, []);

  const measure = useCallback(() => {
    const el = findScrollEl();
    scrollElRef.current = el;
    if (el) {
      setScrollWidth(el.scrollWidth);
      // Make the real region keyboard- and screen-reader-accessible.
      if (el.getAttribute("tabindex") === null) el.setAttribute("tabindex", "0");
      el.setAttribute("role", "region");
      el.setAttribute("aria-label", ariaLabel);
    } else {
      setScrollWidth(0);
    }
  }, [findScrollEl, ariaLabel]);

  // Measure on mount, on resize, and again after the table has laid out.
  useEffect(() => {
    measure();
    const settle = setTimeout(measure, 300);

    let ro;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(containerRef.current);
    }
    window.addEventListener("resize", measure);

    return () => {
      clearTimeout(settle);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  // Real table -> proxy bar
  useEffect(() => {
    const el = scrollElRef.current;
    if (!el) return;
    const onBottomScroll = () => {
      if (syncing.current) return;
      const bar = topBarRef.current;
      if (!bar) return;
      syncing.current = true;
      bar.scrollLeft = el.scrollLeft;
      syncing.current = false;
    };
    el.addEventListener("scroll", onBottomScroll, { passive: true });
    return () => el.removeEventListener("scroll", onBottomScroll);
  }, [scrollWidth]);

  // Proxy bar -> real table
  const onTopScroll = () => {
    if (syncing.current) return;
    const el = scrollElRef.current;
    const bar = topBarRef.current;
    if (!el || !bar) return;
    syncing.current = true;
    el.scrollLeft = bar.scrollLeft;
    syncing.current = false;
  };

  return (
    <div ref={containerRef}>
      {scrollWidth > 0 && (
        <div
          ref={topBarRef}
          onScroll={onTopScroll}
          aria-hidden="true"
          style={{
            position: "sticky",   // pin the top scrollbar
            top: stickyTop,
            zIndex: 6,
            background: "#fff",
            overflowX: "auto",
            overflowY: "hidden",
            height: barHeight,
          }}
        >
          {/* spacer forces the proxy bar's scroll width to match the table */}
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}
      {children}
    </div>
  );
}